/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import config from 'config';

import 'bootstrap/dist/css/bootstrap.min.css';
import { MdOutlineClose, MdOutlineMaximize } from 'react-icons/md';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { BsDownload } from 'react-icons/bs';
import CustomTooltip from '../Reusable/CustomTooltip';
import { assetUrls } from 'containers/NewCreativePreview/constants';
import { useGetConfigQuery } from 'services/api';

type Asset = {
  fileName: string;
  path: string;
  label?: string;
  placeholder?: {
    fileName: string;
    path: string;
  };
};

type MediaItem = {
  type: 'image' | 'video' | 'unknown';
  url: string;
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
};

type TestMetadataAsset = {
  url?: string;
  media_type?: string;
  mimeType?: string;
};

const getAssetTypeFromMediaType = (mediaType?: string) => {
  if (!mediaType) return null;
  if (mediaType.startsWith('image/')) return 'image';
  if (mediaType.startsWith('video/')) return 'video';
  return null;
};

const getAssetTypeFromExtension = (url?: string) => {
  if (!url) return null;
  const normalizedUrl = url.trim().split('?')[0].toLowerCase();
  if (
    normalizedUrl.endsWith('.jpg') ||
    normalizedUrl.endsWith('.jpeg') ||
    normalizedUrl.endsWith('.png') ||
    normalizedUrl.endsWith('.webp') ||
    normalizedUrl.endsWith('.gif')
  ) {
    return 'image';
  }
  if (
    normalizedUrl.endsWith('.mp4') ||
    normalizedUrl.endsWith('.webm') ||
    normalizedUrl.endsWith('.mov')
  ) {
    return 'video';
  }
  return null;
};

const buildPrimaryAssetItem = (
  testMetadata: Record<string, any> | undefined,
): MediaItem[] => {
  const rawAssets = testMetadata?.assets;
  const assets = Array.isArray(rawAssets) ? (rawAssets as TestMetadataAsset[]) : [];
  const objectAssets =
    rawAssets && !Array.isArray(rawAssets) && typeof rawAssets === 'object'
      ? (rawAssets as Record<string, TestMetadataAsset>)
      : null;
  const primaryAsset = assets[0] || objectAssets?.primaryAsset;
  const primaryUrl = primaryAsset?.url?.trim();
  const primaryType = getAssetTypeFromMediaType(
    primaryAsset?.media_type || primaryAsset?.mimeType,
  );

  if (primaryUrl) {
    return [
      {
        type: primaryType || 'unknown',
        url: primaryUrl,
        label: 'Primary Asset',
      },
    ];
  }

  const legacyUrl = testMetadata?.url?.trim();
  if (!legacyUrl) return [];

  const hasNoAssets =
    assets.length === 0 && (!objectAssets || Object.keys(objectAssets).length === 0);

  if (hasNoAssets) {
    console.warn(
      'Primary asset missing in testMetadata.assets; falling back to legacy testMetadata.url',
      legacyUrl,
    );
  }

  return [
    {
      type: getAssetTypeFromExtension(legacyUrl) || 'unknown',
      url: legacyUrl,
      label: 'Primary Asset',
    },
  ];
};

type CarouselProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  assetData?: Asset[];
  assetsLoaded?: boolean;
  assetsLoading?: boolean;
  assetsError?: boolean;
  ensureAssetsLoaded?: () => Promise<Asset[]>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mediaItems: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setMediaItems: React.Dispatch<React.SetStateAction<any[]>>;
  /** Stable document id for this test result — used to cache loads across collapse/expand. */
  testResultId?: string;
  /** When this equals testResultId, assets were already loaded for this row (maybe empty). */
  artifactsLoadedForTestResultId?: string | null;
  onTestResultArtifactsLoaded?: (testResultId: string) => void;
};

export default function CarouselComponent({
  data,
  assetData = [],
  assetsLoaded = false,
  assetsLoading = false,
  assetsError = false,
  ensureAssetsLoaded = async () => [],
  mediaItems,
  setMediaItems,
  testResultId = '',
  artifactsLoadedForTestResultId = null,
  onTestResultArtifactsLoaded = () => {},
}: CarouselProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
  const artifactsLoadedRef = useRef(artifactsLoadedForTestResultId);
  artifactsLoadedRef.current = artifactsLoadedForTestResultId;
  const carouselBodyStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 12,
    paddingBottom: 12,
    boxSizing: 'border-box',
  };
  const carouselTrackStyle: React.CSSProperties = {
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollSnapType: 'x mandatory',
    gap: '10px',
    padding: '0 4px',
    margin: 0,
    width: '100%',
    boxSizing: 'border-box',
    scrollbarWidth: 'none',
  };
  const assetFrameStyle: React.CSSProperties = {
    width: '100%',
    height: '230px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eef4f5',
    borderRadius: '7px',
    boxSizing: 'border-box',
    padding: '12px',
    overflow: 'hidden',
  };
  const assetMediaStyle: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    cursor: 'pointer',
    display: 'block',
  };
  const visibleMediaItems = mediaItems.filter(Boolean) as MediaItem[];

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef) return;
    const scrollAmount = 500 + 10;
    scrollRef.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };
  const { data: appConfig } = useGetConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const cdnUrl = appConfig?.cdnUrl || undefined;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (expandedIndex === null) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setExpandedIndex(prev => (prev && prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setExpandedIndex(prev => {
          if (prev === null || prev === undefined) return 0;
          return prev < visibleMediaItems.length - 1 ? prev + 1 : prev;
        });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault(); // just block up/down scrolling
      } else if (e.key === 'Escape') {
        setExpandedIndex(null); // close overlay
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [expandedIndex, visibleMediaItems.length]);

  // Blob URLs are stored on the parent Card (mediaItems). After a successful load we do not revoke
  // them on carousel unmount—otherwise collapse/expand would invalidate URLs and force a full
  // refetch (createObjectURL again). Card revokes all blobs when data._id changes. We only revoke
  // objectURLs from an in-flight fetch that was abandoned (cleanup runs before loadCompleted).
  useEffect(() => {
    const pendingObjectURLs = new Set<string>();
    let cancelled = false;
    let loadCompleted = false;

    function revokePending() {
      pendingObjectURLs.forEach(url => {
        URL.revokeObjectURL(url);
      });
      pendingObjectURLs.clear();
    }

    function trackObjectURL(url: string) {
      pendingObjectURLs.add(url);
      return url;
    }

    function commitObjectURLs(urls: string[]) {
      urls.forEach(url => pendingObjectURLs.delete(url));
    }

    function stopIfCancelled() {
      if (cancelled) {
        revokePending();
        return true;
      }
      return false;
    }

    async function fetchAssets() {
      // Parent already finished load for this row; reuse mediaItems (skip fetch + new blob URLs).
      if (testResultId && artifactsLoadedRef.current === testResultId) {
        setLoading(false);
        loadCompleted = true;
        return;
      }

      if (data.status === 'Unsupported Test') {
        if (stopIfCancelled()) return;
        setMediaItems([]);
        setLoading(false);
        loadCompleted = true;
        onTestResultArtifactsLoaded(testResultId);
        return;
      }

      if (!assetsLoaded) {
        setLoading(true);
        ensureAssetsLoaded();
        return;
      }

      if (assetsError) {
        setMediaItems([]);
        setLoading(false);
        loadCompleted = true;
        onTestResultArtifactsLoaded(testResultId);
        return;
      }

      setLoading(true);
      if (stopIfCancelled()) return;

      const imageAssets = assetData.filter(
        asset =>
          asset.fileName.endsWith('.jpg') || asset.fileName.endsWith('.webp'),
      );

      const videoAssets = assetData.filter(
        asset =>
          asset.fileName.endsWith('.mp4') || asset.fileName.endsWith('.webm'),
      );

      const meta = data.testMetadata as Record<string, any> | undefined;
      const isClickableVideoTo = meta?.ad_type === 'clickableVideoTo';
      const visibleAssetUrls = assetUrls.filter(
        item => !(isClickableVideoTo && item.id === 'url'),
      );
      const primaryAssetItems = isClickableVideoTo
        ? []
        : buildPrimaryAssetItem(meta);
      const supplementalAssetUrls = visibleAssetUrls.filter(
        item => item.id !== 'url',
      );

      const extraImageItems = await Promise.all(
        supplementalAssetUrls
          .filter(item => getAssetTypeFromExtension(meta?.[item.id]) === 'image')
          .map(async item => {
            const src = meta?.[item.id];
            if (!src) return null;
            return {
              type: 'image' as const,
              url: src,
              label: item.label || '',
            };
          }),
      );

      if (stopIfCancelled()) return;

      const extraVideoItems = await Promise.all(
        supplementalAssetUrls
          .filter(item => getAssetTypeFromExtension(meta?.[item.id]) === 'video')
          .map(async item => {
            const src = meta?.[item.id];
            if (!src) return null;
            return {
              type: 'video' as const,
              url: src,
              label: item.label || '',
              isLoading: false,
            };
          }),
      );
      const finalExternalAssetItems = [
        ...primaryAssetItems,
        ...extraImageItems,
        ...extraVideoItems,
      ];

      const imageItems = await Promise.all(
        imageAssets.map(async asset => {
          const blob = await fetchAssetBlob(
            cdnUrl,
            asset.fileName,
            asset.path,
            () => cancelled,
          );
          if (!blob || stopIfCancelled()) return null;
          const objectURL = trackObjectURL(URL.createObjectURL(blob));

          return {
            type: 'image' as const,
            url: objectURL,
            label: asset.label || '',
          };
        }),
      );

      if (stopIfCancelled()) return;

      const finalImageItems = [
        ...finalExternalAssetItems.filter(Boolean),
        ...imageItems.filter(Boolean),
      ];
      commitObjectURLs(
        finalImageItems
          .map(item => item?.url)
          .filter((url): url is string => Boolean(url)),
      );
      setMediaItems(finalImageItems as any);
      setLoading(false);

      if (videoAssets.length === 0) {
        loadCompleted = true;
        onTestResultArtifactsLoaded(testResultId);
        return;
      }

      const loadingVideoItems = videoAssets.map(asset => ({
        type: 'video' as const,
        url: '',
        label: asset.label || '',
        isLoading: true,
      }));

      setMediaItems(prev => [...prev, ...loadingVideoItems]);

      if (stopIfCancelled()) return;

      const loadedVideos = await Promise.all(
        videoAssets.map(async (asset, idx) => {
          const blob = await fetchAssetBlob(
            cdnUrl,
            asset.fileName,
            asset.path,
            () => cancelled,
          );
          if (!blob || stopIfCancelled()) return null;
          const objectURL = trackObjectURL(URL.createObjectURL(blob));
          return { idx, objectURL };
        }),
      );

      commitObjectURLs(
        loadedVideos
          .filter(Boolean)
          .map(video => video?.objectURL)
          .filter((url): url is string => Boolean(url)),
      );

      setMediaItems(prev => {
        const updated = [...prev];

        loadedVideos.filter(Boolean).forEach(video => {
          if (video) {
            // Find placeholder by matching label
            const placeholderIdx = updated.findIndex(
              (item, i) =>
                item !== null &&
                item.type === 'video' &&
                item.isLoading &&
                i >= finalImageItems.length, // Ensure it's a screen recording video, not extra
            );

            if (placeholderIdx !== -1) {
              const originalItem = updated[placeholderIdx];
              updated[placeholderIdx] = {
                type: 'video' as const,
                url: video.objectURL,
                label: originalItem?.label || '',
              };
            } else {
              updated.push({
                type: 'video' as const,
                url: video.objectURL,
                label: '',
              });
            }
          }
        });

        return updated.filter(
          item => item != null && !(item.type === 'video' && item.isLoading),
        );
      });

      loadCompleted = true;
      onTestResultArtifactsLoaded(testResultId);
    }

    fetchAssets();

    return () => {
      cancelled = true;
      // Successful loads: leave blob: URLs alone (parent still holds them). Aborted loads: free memory.
      if (!loadCompleted) {
        revokePending();
      }
    };
  }, [
    assetData,
    assetsError,
    assetsLoaded,
    assetsLoading,
    data.status,
    ensureAssetsLoaded,
    onTestResultArtifactsLoaded,
    setMediaItems,
    testResultId,
  ]);

  if (data.status === 'Unsupported Test') {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '120px', padding: '24px 16px' }}
      >
        <h5
          style={{
            fontFamily: 'lg-smart, sans-serif',
            fontWeight: 'bold',
            color: 'black',
          }}
        >
          Unsupported Test
        </h5>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={carouselBodyStyle}>
        <div style={carouselTrackStyle}>
          {[1, 2, 3].map((_, idx) => (
            <div
              key={idx}
              style={{
                flex: '0 0 auto',
                textAlign: 'center',
                width: 'calc(25% - 5px)',
                maxWidth: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div className="placeholder-glow" style={assetFrameStyle}>
                <div
                  className="placeholder w-100 h-100"
                  style={{ borderRadius: '7px' }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!loading && visibleMediaItems.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '120px', padding: '24px 16px' }}
      >
        <h5
          style={{
            fontFamily: 'lg-smart, sans-serif',
            fontWeight: 'bold',
            color: 'black',
          }}
        >
          NO TEST ARTIFACTS
        </h5>
      </div>
    );
  }

  const expandedItem =
    expandedIndex !== null ? visibleMediaItems[expandedIndex] : null;

  return (
    <div style={carouselBodyStyle}>
      {/* Left Arrow — inside gutter so media is not clipped */}
      <button
        type="button"
        onClick={() => scroll('left')}
        style={{
          position: 'absolute',
          top: '50%',
          left: 4,
          transform: 'translateY(-50%)',
          zIndex: 2,
          background: 'none',
          border: 'none',
          padding: 4,
        }}
      >
        <FaChevronLeft size={24} style={{ color: '#006e7d' }} />
      </button>

      <div
        ref={ref => setScrollRef(ref)}
        style={carouselTrackStyle}
        className="hide-scrollbar"
      >
        {visibleMediaItems.map((item, index) => (
          <div
            key={index}
            style={{
              flex: '0 0 auto',
              scrollSnapAlign: 'start',
              textAlign: 'center',
              width: 'calc(25% - 5px)',
              maxWidth: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={assetFrameStyle}>
              {item.type === 'image' ? (
                <img
                  src={item.url}
                  draggable={false}
                  alt={`carousel-item-${index}`}
                  style={assetMediaStyle}
                  onClick={() => setExpandedIndex(index)}
                />
              ) : item.type === 'unknown' ? (
                <div
                  role="status"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#4b5563',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedIndex(index)}
                >
                  <strong>Unsupported asset type</strong>
                  <span
                    style={{
                      fontSize: '12px',
                      wordBreak: 'break-all',
                    }}
                  >
                    {item.url}
                  </span>
                </div>
              ) : item.isLoading ? (
                <div
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading video...</span>
                  </div>
                </div>
              ) : (
                <video
                  controls
                  muted
                  draggable={false}
                  preload="auto"
                  poster={item.placeholder}
                  src={item.url}
                  style={assetMediaStyle}
                  onClick={() => setExpandedIndex(index)}
                />
              )}
            </div>
            {item.label && (
              <div style={{ marginTop: 8, fontSize: 12, maxWidth: '100%' }}>
                {item.label}
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => scroll('right')}
        style={{
          position: 'absolute',
          top: '50%',
          right: 4,
          transform: 'translateY(-50%)',
          zIndex: 2,
          background: 'none',
          border: 'none',
          padding: 4,
        }}
      >
        <FaChevronRight size={24} style={{ color: '#006e7d' }} />
      </button>
      {expandedIndex !== null && expandedItem && (
        <div
          onClick={e => {
            if ((e.target as HTMLElement).id === 'expanded-overlay') {
              setExpandedIndex(null);
            }
          }}
          id="expanded-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Close Button - top right corner of image */}
            <button
              onClick={() => setExpandedIndex(null)}
              style={{
                position: 'absolute',
                top: '-50px',
                marginRight: '-1000px',
                fontSize: '25px',
                height: 40,
                width: 40,
                color: '#fff',
                cursor: 'pointer',
                borderRadius: '50px',
                backgroundColor: '#006e7d',
              }}
            >
              <MdOutlineClose size={25} style={{ color: 'white' }} />
            </button>

            {/* Download Button - top right corner of image */}
            <CustomTooltip overlay={'Download Asset'}>
              <a
                href={expandedItem.isLoading ? undefined : expandedItem.url}
                download={expandedItem.isLoading ? undefined : true}
                style={{
                  position: 'absolute',
                  top: '-1px',
                  marginRight: '-1000px',
                  fontSize: '25px',
                  height: 40,
                  width: 40,
                  color: '#fff',
                  cursor: 'pointer',
                  borderRadius: '50px',
                  backgroundColor: '#006e7d',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <BsDownload
                  size={10}
                  style={{ color: 'white', fontWeight: 'bold' }}
                />
              </a>
            </CustomTooltip>
            {/* Left Arrow - beside image */}
            <button
              onClick={() =>
                setExpandedIndex(prev => (prev ? (prev > 0 ? prev - 1 : 0) : 0))
              }
              style={{
                position: 'absolute',
                top: '45%',
                marginLeft: '-1000px',
                fontSize: '18px',
                height: 40,
                width: 40,
                color: '#fff',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
              }}
            >
              <FaChevronLeft size={25} style={{ color: 'white' }} />
            </button>

            {/* Image or video */}
            {expandedItem.type === 'image' ? (
              <img
                src={expandedItem.url}
                alt="Expanded"
                style={{
                  width: '890px',
                  height: '500px',
                  borderRadius: '3px',
                  objectFit: 'contain',
                }}
              />
            ) : expandedItem.type === 'unknown' ? (
              <div
                style={{
                  width: '890px',
                  minHeight: '500px',
                  borderRadius: '3px',
                  background: '#eef4f5',
                  color: '#374151',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                  textAlign: 'center',
                  gap: '12px',
                }}
              >
                <strong>Unsupported asset type</strong>
                <span style={{ wordBreak: 'break-all' }}>{expandedItem.url}</span>
              </div>
            ) : expandedItem.isLoading ? (
              <div
                style={{
                  maxWidth: '70%',
                  maxHeight: '70%',
                  boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              >
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading video...</span>
                </div>
              </div>
            ) : (
              <video
                src={expandedItem.url}
                controls
                muted
                preload="auto"
                poster={expandedItem.placeholder}
                style={{
                  width: '890px',
                  height: '500px',
                  borderRadius: '3px',
                  objectFit: 'contain',
                }}
              />
            )}

            {/* Label */}
            {expandedItem.label && (
              <div
                style={{
                  color: 'white',
                  marginTop: '10px',
                  fontSize: '16px',
                  fontWeight: 'bolder',
                  textAlign: 'center',
                }}
              >
                {expandedItem.label}
              </div>
            )}

            {/* Right Arrow - beside image */}
            <button
              onClick={() =>
                setExpandedIndex(prev => {
                  if (prev === null || prev === undefined) return 0;
                  return prev < visibleMediaItems.length - 1 ? prev + 1 : prev;
                })
              }
              style={{
                position: 'absolute',
                top: '45%',
                marginRight: '-1000px',
                fontSize: '18px',
                height: 40,
                width: 40,
                color: '#fff',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
              }}
            >
              <FaChevronRight size={25} style={{ color: 'white' }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export async function fetchAsset(cdnURL, fileName, path) {
  const token = localStorage.getItem('authorization-token');

  const encodePathSegment = value => {
    try {
      return encodeURIComponent(decodeURIComponent(value));
    } catch {
      return encodeURIComponent(value);
    }
  };

  const normalizedPath = String(path || '').replace(/^\/+|\/+$/g, '');
  const encodedPath = normalizedPath
    ? normalizedPath.split('/').map(encodePathSegment).join('/')
    : '';
  const normalizedFileName = encodePathSegment(
    String(fileName || '').replace(/^\/+/g, ''),
  );
  const cdnUrl = cdnURL
    ? [
        cdnURL.replace(/\/+$/g, ''),
        ...(encodedPath ? [encodedPath] : []),
        normalizedFileName,
      ].join('/')
    : null;
  const serverUrl = `${config.serverUrl}/api/creative-testing/v1/asset/${fileName}?path=${path}`;
  try {
    if (cdnUrl) {
      const cdnResponse = await fetch(cdnUrl);
      if (cdnResponse.ok) return cdnResponse;
      // Fall back to the server proxy if CDN is missing the asset.
    }

    return await fetch(serverUrl, {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    });
  } catch (error) {
    console.error('Error fetching asset:', error);
    return null;
  }
}

export async function fetchAssetBlob(
  cdnUrl,
  fileName,
  path,
  shouldCancel: () => boolean = () => false,
) {
  if (shouldCancel()) return null;
  const response = await fetchAsset(cdnUrl, fileName, path);
  if (!response || !response.ok || shouldCancel()) return null;
  return response.blob();
}

export async function fetchAssetText(
  cdnUrl,
  fileName,
  path,
  shouldCancel: () => boolean = () => false,
) {
  if (shouldCancel()) return null;
  const response = await fetchAsset(cdnUrl, fileName, path);
  if (!response || !response.ok || shouldCancel()) return null;
  return response.text();
}
